import { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { Priority } from "@a46-rss/shared";

interface ArticleListItem {
  id: number;
  title: string;
  url: string;
  published_at: string | null;
  summary: string | null;
  priority: number;
  relevance_score: number;
  is_relevant?: number;
  source_name?: string;
  categories?: Array<{
    category_id: number;
    category_name: string;
    relevance_score: number;
    matched_keywords: string[];
  }>;
}

interface UseArticlesOptions {
  limit?: number;
  priority?: Priority[];
  categoryId?: number;
  sourceId?: number;
  fromDate?: string;
  toDate?: string;
}

export function useArticles(options: UseArticlesOptions = {}) {
  const [articles, setArticles] = useState<ArticleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const fetchedRef = useRef(false);

  // オプションを文字列化して依存配列で使用
  const optionsKey = JSON.stringify({
    limit: options.limit,
    priority: options.priority,
    fromDate: options.fromDate,
    toDate: options.toDate,
  });

  useEffect(() => {
    // 重複実行防止
    if (fetchedRef.current) return;

    const fetchArticles = async () => {
      setLoading(true);
      setError(null);

      try {
        let query = supabase
          .from("articles")
          .select(
            `
            id,
            title,
            url,
            published_at,
            summary,
            priority,
            relevance_score,
            is_relevant,
            sources!inner (
              name
            )
          `,
            { count: "exact" },
          )
          .gte("is_relevant", 0)
          .order("published_at", { ascending: false });

        if (options.priority && options.priority.length > 0) {
          query = query.in("priority", options.priority);
        }

        if (options.fromDate) {
          query = query.gte("published_at", options.fromDate);
        }

        if (options.toDate) {
          query = query.lte("published_at", options.toDate);
        }

        if (options.limit) {
          query = query.limit(options.limit);
        }

        const { data, error: queryError, count } = await query;

        if (queryError) {
          throw new Error(queryError.message);
        }

        const mappedArticles: ArticleListItem[] = (data || []).map(
          (article: any) => ({
            id: article.id,
            title: article.title,
            url: article.url,
            published_at: article.published_at,
            summary: article.summary,
            priority: article.priority,
            relevance_score: article.relevance_score,
            is_relevant: article.is_relevant,
            source_name: article.sources?.name,
          }),
        );

        setArticles(mappedArticles);
        setTotalCount(count || 0);
        fetchedRef.current = true;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "記事の取得に失敗しました",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [optionsKey]);

  const refetch = async () => {
    fetchedRef.current = false;
    setLoading(true);

    try {
      let query = supabase
        .from("articles")
        .select(
          `
          id,
          title,
          url,
          published_at,
          summary,
          priority,
          relevance_score,
          is_relevant,
          sources!inner (
            name
          )
        `,
          { count: "exact" },
        )
        .gte("is_relevant", 0)
        .order("published_at", { ascending: false });

      if (options.priority && options.priority.length > 0) {
        query = query.in("priority", options.priority);
      }

      if (options.fromDate) {
        query = query.gte("published_at", options.fromDate);
      }

      if (options.toDate) {
        query = query.lte("published_at", options.toDate);
      }

      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error: queryError, count } = await query;

      if (queryError) {
        throw new Error(queryError.message);
      }

      const mappedArticles: ArticleListItem[] = (data || []).map(
        (article: any) => ({
          id: article.id,
          title: article.title,
          url: article.url,
          published_at: article.published_at,
          summary: article.summary,
          priority: article.priority,
          relevance_score: article.relevance_score,
          is_relevant: article.is_relevant,
          source_name: article.sources?.name,
        }),
      );

      setArticles(mappedArticles);
      setTotalCount(count || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "記事の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  return { articles, loading, error, totalCount, refetch };
}

export function useSearch(query: string, limit: number = 20) {
  const [results, setResults] = useState<ArticleListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setTotalCount(0);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);

      try {
        const {
          data,
          error: searchError,
          count,
        } = await supabase
          .from("articles")
          .select(
            `
            id,
            title,
            url,
            published_at,
            summary,
            priority,
            relevance_score,
            sources!inner (
              name
            )
          `,
            { count: "exact" },
          )
          .gte("is_relevant", 0)
          .or(
            `title.ilike.%${query}%,summary.ilike.%${query}%,content.ilike.%${query}%`,
          )
          .order("published_at", { ascending: false })
          .limit(limit);

        if (searchError) {
          throw new Error(searchError.message);
        }

        const mappedResults: ArticleListItem[] = (data || []).map(
          (article: any) => ({
            id: article.id,
            title: article.title,
            url: article.url,
            published_at: article.published_at,
            summary: article.summary,
            priority: article.priority,
            relevance_score: article.relevance_score,
            source_name: article.sources?.name,
          }),
        );

        setResults(mappedResults);
        setTotalCount(count || 0);
      } catch (err) {
        setError(err instanceof Error ? err.message : "検索に失敗しました");
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, limit]);

  return { results, loading, error, totalCount };
}

export type { ArticleListItem };
